import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NetworkService {

  private readonly onlineStatus$ = new BehaviorSubject<boolean>(this.getInitialOnlineStatus());
  private onlineEventSubscription: Subscription;
  private offlineEventSubscription: Subscription;
  private connectivityProbeInFlight = false;
  constructor() {
    this.initializeNetworkEvents();
    void this.runConnectivityProbe();
  }

  public initializeNetworkEvents() {
    if (typeof window === 'undefined') {
      return;
    }

    this.onlineEventSubscription = fromEvent(window, 'online').subscribe(() => {
      this.updateOnlineStatus(true);
      void this.runConnectivityProbe();
    });
    this.offlineEventSubscription = fromEvent(window, 'offline').subscribe(() => this.updateOnlineStatus(false));
  }

  private async runConnectivityProbe() {
    if (this.connectivityProbeInFlight || typeof window === 'undefined') {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.updateOnlineStatus(false);
      return;
    }

    this.connectivityProbeInFlight = true;
    try {
      const probeUrl = new URL(window.location.origin);
      probeUrl.searchParams.set('_net', Date.now().toString());
      probeUrl.hash = '';
      await fetch(probeUrl.toString(), {
        method: 'HEAD',
        cache: 'no-store',
        credentials: 'same-origin',
      });
      this.updateOnlineStatus(true);
    } catch {
      this.updateOnlineStatus(false);
    } finally {
      this.connectivityProbeInFlight = false;
    }
  }

  public updateOnlineStatus(status: boolean) {
    if (this.onlineStatus$.value !== status) {
      this.onlineStatus$.next(status);
    }
  }

  public getNetworkStatus() {
    return this.onlineStatus$.asObservable();
  }

  private getInitialOnlineStatus(): boolean {
    if (typeof navigator === 'undefined') {
      return true;
    }

    return navigator.onLine;
  }

  public ngOnDestroy() {
    if (this.onlineEventSubscription) {
      this.onlineEventSubscription.unsubscribe();
    }
    if (this.offlineEventSubscription) {
      this.offlineEventSubscription.unsubscribe();
    }
  }

}
